import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import useStore from '../store';
import { getCategories, getAgencies } from '../graphLoader';
import { getCategoryName, formatCurrency } from '../utils/colors';

function Filters() {
  const {
    graphData,
    showFilters,
    toggleFilters,
    filters,
    setFilters,
    resetFilters
  } = useStore();
  
  const categories = useMemo(() => {
    return graphData ? getCategories(graphData.nodes) : [];
  }, [graphData]);
  
  const agencies = useMemo(() => {
    return graphData ? getAgencies(graphData.nodes) : [];
  }, [graphData]);
  
  const handleCategoryToggle = (category) => {
    const newCategories = filters.categories.includes(category)
      ? filters.categories.filter(c => c !== category)
      : [...filters.categories, category];
    
    setFilters({ categories: newCategories });
  };
  
  const handleAgencyToggle = (agency) => {
    const newAgencies = filters.agencies.includes(agency)
      ? filters.agencies.filter(a => a !== agency)
      : [...filters.agencies, agency];
    
    setFilters({ agencies: newAgencies });
  };
  
  const handleMinValue = (e) => {
    setFilters({ minValue: parseInt(e.target.value) || 0 });
  };
  
  const handleMaxValue = (e) => {
    const value = parseInt(e.target.value);
    setFilters({ maxValue: value || 100000000 });
  };
  
  const handleSearch = (e) => {
    setFilters({ searchQuery: e.target.value });
  };
  
  // FIXED: Only count actual active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.searchQuery && filters.searchQuery.length > 0) count++;
    if (filters.categories.length > 0) count++;
    if (filters.agencies.length > 0) count++;
    if (filters.minValue > 0) count++;
    if (filters.maxValue < 100000000) count++;
    return count;
  }, [filters]);
  
  return (
    <>
      <button className="filters-toggle" onClick={toggleFilters}>
        <span>FILTERS</span>
        {activeFilterCount > 0 && (
          <span className="filter-count">{activeFilterCount}</span>
        )}
      </button>
      
      <AnimatePresence>
        {showFilters && (
          <motion.div
            className="filters-panel"
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          >
            <div className="filters-header">
              <h2>FILTER BIDS</h2>
              <button className="close-btn" onClick={toggleFilters}>×</button>
            </div>
            
            <div className="filters-content">
              <div className="filter-section">
                <label>Search</label>
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search titles, agencies, scopes..."
                  value={filters.searchQuery}
                  onChange={handleSearch}
                />
              </div>
              
              <div className="filter-section">
                <label>Category</label>
                <div className="filter-options">
                  {categories.map(category => (
                    <button
                      key={category}
                      className={`filter-chip ${filters.categories.includes(category) ? 'active' : ''}`}
                      onClick={() => handleCategoryToggle(category)}
                    >
                      {getCategoryName(category)}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="filter-section">
                <label>Contract Value</label>
                <div className="value-range">
                  <div className="value-input-group">
                    <span>Min</span>
                    <input
                      type="number"
                      className="value-input"
                      placeholder="0"
                      value={filters.minValue || ''}
                      onChange={handleMinValue}
                    />
                  </div>
                  <div className="value-input-group">
                    <span>Max</span>
                    <input
                      type="number"
                      className="value-input"
                      placeholder="No limit"
                      value={filters.maxValue === 100000000 ? '' : filters.maxValue}
                      onChange={handleMaxValue}
                    />
                  </div>
                </div>
                
                <div className="quick-filters">
                  <button
                    className="quick-filter-btn"
                    onClick={() => setFilters({ minValue: 1000000, maxValue: 100000000 })}
                  >
                    &gt; $1M
                  </button>
                  <button
                    className="quick-filter-btn"
                    onClick={() => setFilters({ minValue: 5000000, maxValue: 100000000 })}
                  >
                    &gt; $5M
                  </button>
                  <button
                    className="quick-filter-btn"
                    onClick={() => setFilters({ minValue: 0, maxValue: 500000 })}
                  >
                    &lt; $500K
                  </button>
                </div>
              </div>
              
              <div className="filter-section">
                <label>Agencies ({agencies.length} total)</label>
                <div className="agency-list">
                  {agencies.slice(0, 20).map(agency => (
                    <button
                      key={agency}
                      className={`agency-item ${filters.agencies.includes(agency) ? 'active' : ''}`}
                      onClick={() => handleAgencyToggle(agency)}
                    >
                      <span className="agency-checkbox">
                        {filters.agencies.includes(agency) ? '✓' : ''}
                      </span>
                      <span>{agency}</span>
                    </button>
                  ))}
                </div>
                {agencies.length > 20 && (
                  <p className="more-text">+ {agencies.length - 20} more agencies</p>
                )}
              </div>
              
              <div className="filter-actions">
                <button
                  className="reset-btn"
                  onClick={resetFilters}
                  disabled={activeFilterCount === 0}
                >
                  Reset All Filters
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default Filters;